export const RoleProfile = Object.freeze({
  OWNER: 'owner',
  MANAGER: 'manager',
  SHAREHOLDER: 'shareholder',
  FINANCE: 'finance',
  CASHIER: 'cashier',
  WAITER: 'waiter',
} as const)

export type RoleProfileCode = typeof RoleProfile[keyof typeof RoleProfile]

export const ROLE_PROFILE_CODES = Object.freeze(Object.values(RoleProfile)) as readonly RoleProfileCode[]

const ROLE_PROFILE_CODE_SET = new Set<RoleProfileCode>(ROLE_PROFILE_CODES)

export interface RoleProfileResult {
  code: RoleProfileCode
  label: string
}

export interface RoleLike {
  roleName?: string
}

export interface ResolveRoleProfileInput {
  roles?: RoleLike[]
  permissionList?: string[]
}

export interface ResolveXiaochaRoleProfileInput extends ResolveRoleProfileInput {
  viewMode?: 'hq' | 'shop'
}

export type PermissionRequirement = string | string[]

export const ROLE_PROFILE_LABELS: Record<RoleProfileCode, string> = {
  [RoleProfile.OWNER]: '老板',
  [RoleProfile.MANAGER]: '职业经理人',
  [RoleProfile.SHAREHOLDER]: '股东',
  [RoleProfile.FINANCE]: '财务',
  [RoleProfile.CASHIER]: '收银员',
  [RoleProfile.WAITER]: '服务员',
}

const normalizeRoleName = (name?: string) => {
  return (name ?? '').replace(/\s+/g, '').toLowerCase()
}

const hasName = (roleNames: string[], keywords: string[]) => {
  return roleNames.some(roleName => keywords.some(keyword => roleName.includes(keyword)))
}

const hasPermission = (permissions: string[], key: string) => permissions.includes(key)

const hasPermissionPrefix = (permissions: string[], prefix: string) => {
  return permissions.some(permission => permission === prefix || permission.startsWith(`${prefix}_`))
}

const profile = (code: RoleProfileCode): RoleProfileResult => {
  return { code, label: ROLE_PROFILE_LABELS[code] }
}

export const getRoleProfileLabel = (code: RoleProfileCode) => ROLE_PROFILE_LABELS[code]

export const isRoleProfileCode = (value: unknown): value is RoleProfileCode => {
  return typeof value === 'string' && ROLE_PROFILE_CODE_SET.has(value as RoleProfileCode)
}

export const hasPermissionRequirement = (
  permissionList: string[] = [],
  requirement?: PermissionRequirement,
) => {
  if (!requirement) return true
  if (Array.isArray(requirement)) {
    return requirement.some(permission => permissionList.includes(permission))
  }
  return permissionList.includes(requirement)
}

export const resolveRoleProfile = ({
  roles = [],
  permissionList = [],
}: ResolveRoleProfileInput = {}): RoleProfileResult => {
  const roleNames = roles.map(role => normalizeRoleName(role.roleName)).filter(Boolean)

  if (hasName(roleNames, ['老板', '店主', '超级管理员', '总经理', 'owner', 'superadmin'])) return profile(RoleProfile.OWNER)
  if (hasName(roleNames, ['股东', '投资人', 'shareholder', 'partner'])) return profile(RoleProfile.SHAREHOLDER)
  if (hasName(roleNames, ['财务', '会计', '出纳', 'finance', 'accountant'])) return profile(RoleProfile.FINANCE)
  if (hasName(roleNames, ['职业经理人', '店长', '经理', '管理员', 'manager', 'admin'])) return profile(RoleProfile.MANAGER)
  if (hasName(roleNames, ['收银', 'cashier'])) return profile(RoleProfile.CASHIER)
  if (hasName(roleNames, ['服务员', '服务生', 'waiter', 'server'])) return profile(RoleProfile.WAITER)

  if (
    (hasPermission(permissionList, 'business') || hasPermissionPrefix(permissionList, 'dashboard'))
    && hasPermissionPrefix(permissionList, 'setting')
  ) {
    return profile(RoleProfile.MANAGER)
  }
  if (
    hasPermissionPrefix(permissionList, 'procurement')
    || hasPermissionPrefix(permissionList, 'stock')
    || hasPermissionPrefix(permissionList, 'basic_stock')
  ) {
    return profile(RoleProfile.MANAGER)
  }

  return profile(RoleProfile.WAITER)
}

const HQ_FINANCE_PROFILE_PERMISSIONS = [
  'hq_settlement_view',
  'hq_settlement_manage',
]

const HQ_OPERATION_PROFILE_PERMISSIONS = [
  'hq_brand_facade_manage',
  'hq_stored_value_rules_manage',
  'hq_cashback_rules_manage',
  'hq_coupon_templates_manage',
  'hq_coupon_rules_manage',
  'hq_coupon_issue_manage',
  'hq_member_levels_manage',
  'hq_member_assets_view',
  'hq_member_dedupe_manage',
  'hq_reservation_rules_manage',
  'hq_operation_targets_manage',
  'chain_member_tag_manage',
]

/**
 * Resolve the coarse role profile consumed by Xiaocha.
 *
 * HQ custom role names are user-defined and therefore not a stable contract. In HQ scope,
 * use domain permission codes only; unknown/minimal HQ roles stay on the restricted-by-tool
 * HQ owner persona instead of falling through to the shop staff persona.
 */
export const resolveXiaochaRoleProfile = ({
  viewMode,
  roles = [],
  permissionList = [],
}: ResolveXiaochaRoleProfileInput = {}): RoleProfileResult => {
  if (viewMode !== 'hq') {
    return resolveRoleProfile({ roles, permissionList })
  }

  const hasFinance = HQ_FINANCE_PROFILE_PERMISSIONS.some(permission =>
    permissionList.includes(permission),
  )
  const hasOperation = HQ_OPERATION_PROFILE_PERMISSIONS.some(permission =>
    permissionList.includes(permission),
  )

  if (hasFinance && !hasOperation) return profile(RoleProfile.FINANCE)
  if (hasOperation && !hasFinance) return profile(RoleProfile.MANAGER)
  return profile(RoleProfile.OWNER)
}
